import React from 'react';
import { Box, Card, CardContent, CardActionArea, Typography, List } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Items to manage – add more entries here as needed
const MANAGE_ITEMS = [
  {
    path: '/manage/civi-migration-jobs',
    label: 'Civi migration jobs',
    description: 'Scheduled migration queue, run stats, logs, and reschedule',
    perms: ['reports:view'],
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
  {
    path: '/manage/users',
    label: 'User management',
    description: 'Manage staff users and permissions',
    perms: ['users:manage'],
  },
  {
    path: '/manage/roles',
    label: 'Role management',
    description: 'Roles and default permissions',
    perms: ['users:manage'],
  },
  {
    path: '/manage/email-templates',
    label: 'Email Templates',
    description: 'Add, edit, and delete reusable email templates',
    perms: ['reports:view'],
  },
  {
    path: '/manage/calendar-events',
    label: 'Calendar Events & Newsletters',
    description: 'Manage events and sessions, map products, and schedule event newsletters',
    perms: ['reports:view', 'calendar:manage'],
  },
  {
    path: '/manage/alerts-reminders',
    label: 'Alerts and reminders',
    description: 'Read-only audit log of automated reminder emails sent by the server',
    perms: ['reports:view'],
  },
  {
    path: '/manage/rosters',
    label: 'Rosters',
    description: 'View Trustees, Trustee-on-Duty, and Event Coordinator rosters by year',
    // Matches the backend ROSTER_PERM so anyone allowed to hit the roster APIs (incl. Coordinators
    // via communications:access) sees the card.
    perms: ['reports:view', 'event_reports:access', 'sponsors_reports:access', 'communications:access'],
  },
  {
    path: '/manage/kiosk-prime-products',
    label: 'Kiosk Prime Products',
    description: 'Manage promoted kiosk products with start and end dates',
    perms: ['reports:view'],
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
  {
    path: '/manage/sponsor-benefits',
    label: 'Sponsor Benefits',
    description: 'Configure sponsor product/variation benefits linked to event reports',
    perms: ['reports:view'],
  },
  {
    path: '/manage/life-membership-eligibility',
    label: 'Life-membership Eligibility',
    description: 'Donors who qualify for Life Membership by giving level; preview & send eligibility emails',
    perms: ['reports:view'],
  },
  {
    path: '/manage/civi-wc-product-mapping',
    label: 'CiviCRM migration',
    description: 'Map CiviCRM financial types and contribution pages to WooCommerce products',
    perms: ['reports:view'],
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
];

function canAccess(perms, role, requiredPerms) {
  if (String(role || '').toLowerCase() === 'admin') return true;
  if (Array.isArray(perms) && perms.includes('admin:full')) return true;
  return requiredPerms.some((p) => perms.includes(p));
}

export default function ManagePage() {
  const navigate = useNavigate();
  const { staff } = useAuth();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const visible = MANAGE_ITEMS.filter((item) => !item.hidden && canAccess(perms, staff?.role, item.perms || []));

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Manage</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage special programs and data views
        </Typography>
      </Box>

      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visible.map((item) => (
          <Card
            key={item.path}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                boxShadow: 2,
                borderColor: 'primary.main',
              },
            }}
          >
            <CardActionArea onClick={() => navigate(item.path)} sx={{ display: 'block' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  )}
                </Box>
                <ChevronRightIcon color="action" />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </List>
    </PageLayout>
  );
}
